import { AfterContentChecked, ChangeDetectorRef, Component, DestroyRef, inject, ViewChild } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { filter, interval, Observable, of, take } from "rxjs";
import DialogService from "../services/dialog.service";
import LockService from "../../Dispatch/Shared/services/lock.service";
import { ArcusAuthService } from "../../core/arcusauth.service";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { UpdateStatusService } from "../services/updateStatus.service";
import { ArcusLayoutService } from "../services/arcus-layout.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Constants } from "../../../constants";
import { MatSidenavContainer, MatSidenav, MatSidenavContent } from "@angular/material/sidenav";
import { NavigationComponent } from "../navigation/navigation.component";
import { NotificationComponent } from "../notification/notification.component";
import { ArcusToolbarComponent } from "../arcus-toolbar/arcus-toolbar.component";

@Component({
    selector: "arcus-layout",
    templateUrl: "./arcus-layout.component.html",
    styleUrl: "./arcus-layout.component.scss",
    imports: [
        MatSidenavContainer,
        MatSidenav,
        NavigationComponent,
        NotificationComponent,
        MatSidenavContent,
        ArcusToolbarComponent,
        RouterOutlet,
    ],
})
export class ArcusLayoutComponent implements AfterContentChecked {
    @ViewChild(RouterOutlet, { static: false }) routerOutlet: RouterOutlet;

    destroyRef = inject(DestroyRef);

    constructor(
        private dialogService: DialogService,
        public router: Router,
        private cdr: ChangeDetectorRef,
        private lockService: LockService,
        private authService: ArcusAuthService,
        private swUpdate: SwUpdate,
        private updateStatusService: UpdateStatusService,
        public layoutService: ArcusLayoutService
    ) {
        if (this.swUpdate.isEnabled) {
            this.swUpdate.versionUpdates
                .pipe(
                    filter((evt): evt is VersionReadyEvent => evt.type === "VERSION_READY"),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe((evt) => {
                    var navEnd = this.router.events
                        .pipe(
                            filter((evt) => evt instanceof NavigationEnd),
                            take(1)
                        )
                        .subscribe(() => {
                            this.windowReload();
                        });
                    this.handleGridCache(evt.currentVersion.appData, evt.latestVersion.appData);
                    if (this.checkVersionDifferences(evt.currentVersion.appData, evt.latestVersion.appData)) {
                        this.updateStatusService.setOutOfSync(true);
                        this.dialogService
                            .mdAlert(
                                "Arcus is out of sync and requires an update.",
                                "Incompatible Version",
                                "Reload",
                                "",
                                true
                            )
                            .afterClosed()
                            .subscribe(() => {
                                navEnd.unsubscribe();
                                this.windowReload();
                                this.updateStatusService.setOutOfSync(false); // Not strictly needed as the reload should bypass this
                            });
                    }
                });

            this.swUpdate.checkForUpdate();
            interval(2 * 60 * 1000)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.swUpdate.checkForUpdate());
        }
    }

    windowReload() {
        window.location.reload();
    }

    ngAfterContentChecked(): void {
        this.cdr.detectChanges();
    }

    checkVersionDifferences(currentVersion, latestVersion) {
        if (latestVersion.requiresUpdate && currentVersion.version != latestVersion.version) {
            return true;
        } else {
            return false;
        }
    }

    handleGridCache(currentVersion, latestVersion) {
        if (latestVersion.deleteGridCache && currentVersion.version != latestVersion.version) {
            const mobileStr = Constants.isMobile() ? "Mobile" : "";
            localStorage.removeItem(`OrderGrid${mobileStr}Filters`);
            localStorage.removeItem(`LoadGrid${mobileStr}Filters`);
            localStorage.removeItem(`SalesGrid${mobileStr}Filters`);
            localStorage.removeItem(`TemplateGrid${mobileStr}Filters`);
            localStorage.removeItem(`TaskingGrid${mobileStr}Filters`);
            console.log("Cleared the grid cache");
        }
    }

    checkIfCanDeactivate(): Observable<boolean> {
        const childComponent = this.routerOutlet.component as any; // Exposed as type 'any' by default
        let canDeactivateObs: Observable<boolean>;

        if (typeof childComponent?.canDeactivate === "function") {
            const result = childComponent.canDeactivate();
            canDeactivateObs = result instanceof Observable ? result : of(result as boolean);
        } else {
            canDeactivateObs = of(true);
        }

        return canDeactivateObs;
    }

    // TODO: Simplify using rxjs operators
    logout() {
        const canDeactivateObs = this.checkIfCanDeactivate();

        canDeactivateObs.subscribe((canLogOut) => {
            if (!canLogOut) {
                return;
            }

            this.dialogService
                .mdConfirm("Logout", "Are you sure you want to logout of ARCUS?", "Logout", "Cancel", false, "logout")
                .afterClosed()
                .subscribe((result) => {
                    if (result) {
                        this.lockService.removeLock().then(() => {
                            localStorage.clear();
                            this.lockService.logoutSource.next(true);
                            this.authService.logout();
                        });
                    }
                });
        });
    }
}
